import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, Search, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useCartStore } from '../stores/cartStore';
import { useAuthStore } from '../stores/authStore';
import { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react'; // install with: npm install @headlessui/react

export function StoreHeader() {
  const navigate = useNavigate();
  const totalItems = useCartStore((state) => state.getTotalItems());
  const { isAuthenticated, logout, isAdmin } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Rotating banner state
  const messages = [
    "For enquiries or orders call 011 8182 959",
    "Same-day delivery in Nairobi. Ts and Cs apply.",
    "For enquiries or orders call 011 8182 959",
    "Free delivery for shopping over KES 5,000/=",
    "For enquiries or orders call 011 8182 959"
  ];

  const [bannerIndex, setBannerIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setBannerIndex((prev) => (prev + 1) % messages.length);
    }, 15000);
    return () => clearInterval(interval);
  }, [messages.length]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-stone-200">
      {/* Rotating Red Banner */}
      <div className="bg-red-600 text-white text-center text-sm py-2 shadow-sm overflow-hidden">
        <Transition
          key={bannerIndex}
          appear={true}
          show={true}
          enter="transition-opacity duration-700 transform"
          enterFrom="opacity-0 translate-y-2"
          enterTo="opacity-100 translate-y-0"
          leave="transition-opacity duration-700 transform"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-2"
        >
          <span className="block">{messages[bannerIndex]}</span>
        </Transition>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <h1 className="text-2xl font-bold text-emerald-900 tracking-tight">
              MEGA<span className="text-red-600">MART</span>
            </h1>
          </Link>
          <Link to="/plug" className="flex-shrink-0" data-testid="logo-link">
            <h1 className="text-2xl font-bold text-emerald-900 tracking-tight">
              M<span className="text-red-600">-Juza</span>
            </h1>
          </Link>

          {/* Search - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex w-full max-w-md mx-auto mt-3 md:mt-0">
            <div className="relative w-full">
              {/* Left icon */}
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />

              {/* Input */}
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-16 bg-stone-50 border-stone-200 w-full"
                data-testid="search-input"
              />

              {/* Embedded button */}
              <Button
                type="submit"
                size="sm"
                variant="default"
                className="absolute right-1 top-1/2 -translate-y-1/2 px-3 py-1 text-xs"
                data-testid="search-btn"
              >
                Go
              </Button>
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Cart */}
            <Link to="/cart" data-testid="cart-link">
              <Button variant="ghost" size="icon" className="relative">
                <ShoppingCart className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center cart-badge">
                    {totalItems}
                  </span>
                )}
              </Button>
            </Link>

            {/* User Menu - Desktop */}
            <div className="hidden md:flex items-center gap-2">
              {isAuthenticated ? (
                <>
                  {isAdmin() && (
                    <Link to="/admin" data-testid="admin-link">
                      <Button variant="outline" size="sm">Admin</Button>
                    </Link>
                  )}
                  <Link to="/orders" data-testid="orders-link">
                    <Button variant="ghost" size="sm">My Orders</Button>
                  </Link>
                  <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Link to="/login" data-testid="login-link">
                  <Button variant="outline" size="sm">
                    <User className="h-4 w-4 mr-2" />
                    Login
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <nav className="flex flex-col gap-4 mt-8">
                  {/* Mobile Search */}
                  <form onSubmit={handleSearch} className="mb-4">
                    <Input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      data-testid="mobile-search-input"
                    />
                  </form>

                  <Link to="/" onClick={() => setMobileMenuOpen(false)} className="py-2 text-lg font-medium">Home</Link>
                  <Link to="/products" onClick={() => setMobileMenuOpen(false)} className="py-2 text-lg font-medium">Products</Link>
                  <Link to="/cart" onClick={() => setMobileMenuOpen(false)} className="py-2 text-lg font-medium">Cart ({totalItems})</Link>

                  {isAuthenticated ? (
                    <>
                      <Link to="/orders" onClick={() => setMobileMenuOpen(false)} className="py-2 text-lg font-medium">My Orders</Link>
                      {isAdmin() && (
                        <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="py-2 text-lg font-medium">Admin Panel</Link>
                      )}
                      <Button variant="outline" onClick={handleLogout} className="mt-4">Logout</Button>
                    </>
                  ) : (
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full mt-4">Login / Register</Button>
                    </Link>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Search - Mobile (always visible below logo/actions) */}
        <form onSubmit={handleSearch} className="flex md:hidden w-full max-w-md mx-auto mt-3">
          <div className="relative w-full">
            {/* Left icon */}
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />

            {/* Input */}
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-16 bg-stone-50 border-stone-200 w-full"
              data-testid="search-input"
            />

            {/* Embedded button */}
            <Button
              type="submit"
              size="sm"
              variant="default"
              className="absolute right-1 top-1/2 -translate-y-1/2 px-3 py-1 text-xs"
              data-testid="search-btn"
            >
              Go
            </Button>
          </div>
        </form>
      </div>
      <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5583478938035233"
        crossorigin="anonymous"></script>
    </header>
  );
}
